/**
=========================================================
* IDP - v2.2.0
=========================================================
*/

import { useState, useEffect } from "react";
import apiClient from "services/api";
import PdfViewer from "components/PdfViewer";

// @mui material components
import {
  Box,
  Card,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import JsonViewer from "components/JsonViewer";
import { styled } from "@mui/material/styles";

// IDP components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const DropZone = styled("div")(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

function DocumentParser() {
  const [file, setFile] = useState(null);
  const [documentType, setDocumentType] = useState("");
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);

  const [processingOptions, setProcessingOptions] = useState({
    confidenceThreshold: 0.8,
    ocrEnabled: true,
  });

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const types = await apiClient.getDocumentTypes();
        setDocumentTypes(types);
        if (types.length > 0) {
          setDocumentType(types[0].id);
        }
      } catch (error) {
        setError("Failed to fetch document types");
      }
    };
    fetchDocumentTypes();
  }, []);

  const handleFileDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (
      droppedFile &&
      [
        "application/pdf",
        "image/png",
        "image/jpeg",
        "image/jpg",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(droppedFile.type)
    ) {
      setFile(droppedFile);
      setFileUrl(URL.createObjectURL(droppedFile));
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileUrl(URL.createObjectURL(selectedFile));
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleProcessDocument = async () => {
    if (!file || !documentType) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiClient.processDocument(file, documentType, processingOptions);

      if (response.success !== false && response.document_id) {
        // Show initial response
        setResult({
          success: true,
          status: "Processing",
          message: "Document processing started",
          document_id: response.document_id,
        });

        // Start polling for document status
        await apiClient.pollDocumentStatus(response.document_id, (pollResult) => {
          console.log("Poll update:", pollResult);

          if (!pollResult) {
            setError("Invalid response from server");
            setLoading(false);
            return;
          }

          // Update result state with latest status
          setResult(pollResult);

          // Handle different status cases
          switch (pollResult.status) {
            case "Processing":
              setLoading(true);
              setError(null);
              break;

            case "Completed":
              setLoading(false);
              // Fetch the complete document data
              apiClient
                .getDocument(pollResult.id)
                .then((documentData) => {
                  console.log("Fetched document data:", documentData);

                  if (documentData.parsed_data) {
                    // Just use the document data directly without nesting
                    setResult(documentData);
                    setError(null);
                  } else {
                    console.log("No parsed_data in document:", documentData);
                    setError("No data extracted from document");
                  }
                })
                .catch((e) => {
                  console.error("Error fetching document data:", e);
                  setError("Error fetching document data");
                });
              break;
            case "Error":
              setLoading(false);
              setError(pollResult.error || "Document processing failed");
              break;

            default:
              setLoading(false);
              setError(`Unknown status: ${pollResult.status}`);
          }
        });
      } else {
        setError(response.error || "Failed to process document");
        setLoading(false);
      }
    } catch (err) {
      const errorMessage = err.message || "Failed to process document";
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Document Parser
                </MDTypography>
              </MDBox>
              <MDBox pt={3} pb={3} px={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <DropZone
                      onDrop={!loading ? handleFileDrop : undefined}
                      onDragOver={!loading ? handleDragOver : undefined}
                      onClick={() => !loading && document.getElementById("file-input").click()}
                      style={{
                        opacity: loading ? 0.6 : 1,
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                    >
                      <input
                        id="file-input"
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={handleFileSelect}
                        style={{ display: "none" }}
                      />
                      <MDTypography variant="h6" gutterBottom>
                        {file ? file.name : "Drag and drop your file here"}
                      </MDTypography>
                      <MDTypography variant="body2" color="text">
                        Supported formats: PDF, PNG, JPG, JPEG
                      </MDTypography>
                      <MDButton variant="gradient" color="info" sx={{ mt: 2 }} disabled={loading}>
                        Browse Files
                      </MDButton>
                    </DropZone>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                      <InputLabel htmlFor="document-type">Document Type</InputLabel>
                      <Select
                        id="document-type"
                        value={documentType}
                        label="Document Type"
                        onChange={(e) => setDocumentType(e.target.value)}
                        sx={{
                          height: "45px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0,0,0,0.23)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0,0,0,0.87)",
                          },
                        }}
                        disabled={loading}
                      >
                        {documentTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <MDButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      onClick={handleProcessDocument}
                      disabled={!file || !documentType || loading}
                    >
                      {loading ? "Processing..." : "Process Document"}
                    </MDButton>

                    {error && (
                      <MDBox mt={2}>
                        <MDTypography color="error" variant="button" fontWeight="regular">
                          {error}
                        </MDTypography>
                      </MDBox>
                    )}
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {(result || fileUrl) && (
          <Grid container spacing={3} mt={1}>
            {fileUrl && (
              <Grid item xs={12} md={6}>
                <Card>
                  <MDBox p={3}>
                    {file?.type === "application/pdf" ? (
                      <PdfViewer file={fileUrl} title="Document Preview" />
                    ) : (
                      <Box sx={{ height: "calc(100vh - 300px)", overflowY: "auto" }}>
                        <img
                          src={fileUrl}
                          alt="Document Preview"
                          style={{ maxWidth: "100%", height: "auto" }}
                        />
                      </Box>
                    )}
                  </MDBox>
                </Card>
              </Grid>
            )}
            {result && (
              <Grid item xs={12} md={fileUrl ? 6 : 12}>
                <Card>
                  <MDBox p={3}>
                    <JsonViewer data={result} />
                  </MDBox>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default DocumentParser;
